import type { ReactNode } from 'react'
import { motion } from 'framer-motion'
import { Activity, ChevronRight, ShieldAlert, Sparkles } from 'lucide-react'
import type { ProjectCard as Card } from '../lib/api'
import { cn } from '../lib/utils'

export function ProjectCardView({
  card,
  index,
  selected,
  onOpen,
}: {
  card: Card
  index: number
  selected?: boolean
  onOpen?: (card: Card) => void
}) {
  return (
    <motion.article
      layout
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onClick={() => onOpen?.(card)}
      onKeyDown={(e) => {
        if (!onOpen) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen(card)
        }
      }}
      initial={{ opacity: 0, y: 24, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay: index * 0.06, type: 'spring', stiffness: 120, damping: 18 }}
      whileHover={{ y: -6, scale: 1.01 }}
      className={cn(
        'glass neon-ring relative overflow-hidden rounded-3xl p-5 text-left',
        onOpen && 'cursor-pointer',
        selected && 'ring-2 ring-[var(--color-cyan)]',
        card.mvp_ready && 'shadow-[0_0_40px_rgba(46,230,197,0.18)]',
      )}
    >
      <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-[radial-gradient(circle,rgba(46,230,197,0.25),transparent_70%)]" />
      <div className="relative flex items-start justify-between gap-3">
        <div>
          <p className="font-[family-name:var(--font-mono)] text-[11px] uppercase tracking-[0.2em] text-[var(--color-mist)]">
            {card.id.slice(0, 28)}
          </p>
          <h3 className="mt-2 max-w-[18rem] font-[family-name:var(--font-display)] text-xl font-bold leading-snug text-[var(--color-foam)]">
            {card.name}
          </h3>
        </div>
        <span
          className={cn(
            'rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide',
            card.mvp_ready
              ? 'bg-[rgba(46,230,197,0.16)] text-[var(--color-cyan)]'
              : 'bg-[rgba(255,179,71,0.16)] text-[var(--color-amber)]',
          )}
        >
          {card.mvp_ready ? 'MVP ready' : card.status || 'building'}
        </span>
      </div>

      <div className="relative mt-5 grid grid-cols-2 gap-3 text-sm">
        <Meta icon={<Activity className="h-3.5 w-3.5" />} label="Phase" value={card.phase} />
        <Meta
          icon={<Sparkles className="h-3.5 w-3.5" />}
          label="Skill grade"
          value={card.skill_grade_avg != null ? card.skill_grade_avg.toFixed(2) : '—'}
        />
        <Meta
          icon={<ShieldAlert className="h-3.5 w-3.5" />}
          label="Risk"
          value={card.risk}
        />
        <Meta label="Coverage" value={`${Math.round(card.requirement_coverage_pct || 0)}%`} />
      </div>

      <div className="relative mt-5">
        <div className="mb-1 flex justify-between text-[11px] uppercase tracking-wider text-[var(--color-mist)]">
          <span>Requirement coverage</span>
          <span>{Math.round(card.requirement_coverage_pct || 0)}%</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-white/5">
          <motion.div
            className="h-full rounded-full bg-gradient-to-r from-[var(--color-cyan)] to-[var(--color-amber)]"
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(100, card.requirement_coverage_pct || 0)}%` }}
            transition={{ duration: 0.9, ease: 'easeOut' }}
          />
        </div>
      </div>

      {onOpen && (
        <div className="relative mt-4 flex items-center justify-end gap-1 text-xs font-semibold text-[var(--color-cyan)]">
          Open metrics
          <ChevronRight className="h-3.5 w-3.5" />
        </div>
      )}
    </motion.article>
  )
}

function Meta({
  label,
  value,
  icon,
}: {
  label: string
  value: string
  icon?: ReactNode
}) {
  return (
    <div className="rounded-2xl border border-[var(--color-line)] bg-white/[0.03] px-3 py-2">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-[var(--color-mist)]">
        {icon}
        {label}
      </div>
      <div className="mt-1 font-semibold capitalize text-[var(--color-foam)]">{value}</div>
    </div>
  )
}
